import classNames from 'classnames';
import { useMemo } from 'react';
import { TopHolders } from '@/modules/issuer-dashboard/components/class-tabs/TopHolders.tsx';
import { IssuedCapitalOverTimeChart } from '@/modules/issuer-dashboard/components/securities-dashboard/IssuedCapitalOverTimeChart.tsx';
import { NavOverTimeChart } from '@/modules/issuer-dashboard/components/securities-dashboard/NavOverTimeChart.tsx';
import { useIssuerGetIssuedCapitalSummaryQuery } from '@/modules/issuer-dashboard/queries/dashboard.ts';
import { Skeleton } from 'ui';
import { useDeferredSectionCount } from 'ui/hooks';
import { CopyIcon } from 'ui/icons';
import { copyTextToClipBoard, goodFormatNumber, goodFormatUSDCurrency } from 'ui/utils';
import { RegisterClassResponse } from 'api';

interface ClassOverviewProps {
  classItem?: RegisterClassResponse;
  isLoading: boolean;
}

export const ClassOverview = ({ classItem, isLoading }: ClassOverviewProps) => {
  const visibleSectionCount = useDeferredSectionCount(4);

  return (
    <div className="flex flex-col gap-6 px-3 sm:px-6 py-4 sm:py-6">
      {visibleSectionCount >= 1 && <ClassInformation isLoading={isLoading} classItem={classItem} />}
      {visibleSectionCount >= 3 && (
        <IssuedCapitalOverTimeChart
          register_class_ids={classItem?.id ? [classItem?.id] : undefined}
          fullHeight={false}
        />
      )}
      {visibleSectionCount >= 4 && (
        <NavOverTimeChart register_class_ids={classItem?.id ? [classItem?.id] : undefined} />
      )}
      {visibleSectionCount >= 2 && (
        <TopHolders register_class_id={classItem?.id ? classItem?.id : undefined} />
      )}
    </div>
  );
};

export const ClassInformation = ({ classItem }: ClassOverviewProps) => {
  const { data: summaryData, isFetching } = useIssuerGetIssuedCapitalSummaryQuery({
    register_class_id: classItem?.id || undefined,
  });

  const info = useMemo(() => {
    if (!summaryData?.data) return;

    const result = Object.fromEntries(summaryData?.data?.map(item => [item.type, item.value || 0]));
    return result;
  }, [summaryData]);

  return (
    <div
      className={classNames(
        'w-full flex flex-wrap items-center justify-between',
        'border border-gray-200 rounded-xl',
      )}
    >
      <div
        className={
          'px-4 py-3 md:w-1/3 sm:w-1/2 w-full flex gap-4 justify-between items-start font-[Inter] ' +
          'group/block sm:border-r border-gray-200 border-b'
        }
      >
        <div className="flex flex-col gap-1 w-full">
          <div className="text-xs leading-5">Subscriptions</div>
          {isFetching ? (
            <Skeleton width={'70%'} height={28} />
          ) : (
            <div className="text-xl">{goodFormatUSDCurrency(info?.['Applications'] || 0)}</div>
          )}
        </div>

        {!isFetching && (
          <div className="group-hover/block:block hidden">
            <span
              className="cursor-pointer hover:opacity-70"
              onClick={() =>
                copyTextToClipBoard(goodFormatUSDCurrency(info?.['Applications'] || 0), {
                  successMessage: 'Copied successfully',
                })
              }
            >
              <img src={CopyIcon} alt="Copy" className="w-6 h-6 min-w-6 min-h-6 " />
            </span>
          </div>
        )}
      </div>
      <div
        className={
          'px-4 py-3 md:w-1/3 sm:w-1/2 w-full flex gap-4 justify-between items-start font-[Inter] ' +
          'group/block border-r md:border-r-gray-200 border-r-transparent border-b border-b-gray-200'
        }
      >
        <div className="flex flex-col gap-1 w-full">
          <div className="text-xs leading-5">Reinvestments</div>
          {isFetching ? (
            <Skeleton width={'70%'} height={28} />
          ) : (
            <div className="text-xl">{goodFormatUSDCurrency(info?.['DRP/Reinvestment'] || 0)}</div>
          )}
        </div>
        {!isFetching && (
          <div className="group-hover/block:block hidden">
            <span
              className="cursor-pointer hover:opacity-70"
              onClick={() =>
                copyTextToClipBoard(goodFormatUSDCurrency(info?.['DRP/Reinvestment'] || 0), {
                  successMessage: 'Copied successfully',
                })
              }
            >
              <img src={CopyIcon} alt="Copy" className="w-6 h-6 min-w-6 min-h-6 " />
            </span>
          </div>
        )}
      </div>
      <div
        className={
          'px-4 py-3 md:w-1/3 sm:w-1/2 w-full flex gap-4 justify-between items-start font-[Inter] ' +
          'group/block border-r sm:border-r-gray-200 md:border-r-transparent border-r-transparent border-b-gray-200 border-b'
        }
      >
        <div className="flex flex-col gap-1 w-full">
          <div className="text-xs leading-5">Buyback / Redemptions</div>
          {isFetching ? (
            <Skeleton width={'70%'} height={28} />
          ) : (
            <div className="text-xl">
              {goodFormatUSDCurrency(info?.['Buyback/Redemption'] || 0)}
            </div>
          )}
        </div>
        {!isFetching && (
          <div className="group-hover/block:block hidden">
            <span
              className="cursor-pointer hover:opacity-70"
              onClick={() =>
                copyTextToClipBoard(goodFormatUSDCurrency(info?.['Buyback/Redemption'] || 0), {
                  successMessage: 'Copied successfully',
                })
              }
            >
              <img src={CopyIcon} alt="Copy" className="w-6 h-6 min-w-6 min-h-6 " />
            </span>
          </div>
        )}
      </div>
      <div
        className={
          'px-4 py-3 md:w-1/3 sm:w-1/2 w-full flex gap-4 justify-between items-start font-[Inter] ' +
          'group/block border-r md:border-r-gray-200 border-r-transparent md:border-b-transparent border-b'
        }
      >
        <div className="flex flex-col gap-1 w-full">
          <div className="text-xs leading-5">Implied Value</div>
          {isFetching ? (
            <Skeleton width={'70%'} height={28} />
          ) : (
            <div className="text-xl">{goodFormatUSDCurrency(info?.['Implied Value'] || 0)}</div>
          )}
        </div>
        {!isFetching && (
          <div className="group-hover/block:block hidden">
            <span
              className="cursor-pointer hover:opacity-70"
              onClick={() =>
                copyTextToClipBoard(goodFormatUSDCurrency(info?.['Implied Value'] || 0), {
                  successMessage: 'Copied successfully',
                })
              }
            >
              <img src={CopyIcon} alt="Copy" className="w-6 h-6 min-w-6 min-h-6 " />
            </span>
          </div>
        )}
      </div>
      <div
        className={
          'px-4 py-3 md:w-1/3 sm:w-1/2 w-full flex gap-4 justify-between items-start font-[Inter] ' +
          'group/block border-r sm:border-r-gray-200 border-r-transparent border-b border-b-gray-200 sm:border-b-transparent'
        }
      >
        <div className="flex flex-col gap-1 w-full">
          <div className="text-xs leading-5">Current NAV</div>
          {isFetching ? (
            <Skeleton width={'70%'} height={28} />
          ) : (
            <div className="text-xl">{goodFormatUSDCurrency(info?.['Current NAV'] || 0)}</div>
          )}
        </div>
        {!isFetching && (
          <div className="group-hover/block:block hidden">
            <span
              className="cursor-pointer hover:opacity-70"
              onClick={() =>
                copyTextToClipBoard(goodFormatUSDCurrency(info?.['Current NAV'] || 0), {
                  successMessage: 'Copied successfully',
                })
              }
            >
              <img src={CopyIcon} alt="Copy" className="w-6 h-6 min-w-6 min-h-6 " />
            </span>
          </div>
        )}
      </div>
      <div className="px-4 py-3 md:w-1/3 sm:w-1/2 w-full flex gap-4 justify-between items-start font-[Inter] group/block">
        <div className="flex flex-col gap-1 w-full">
          <div className="text-xs leading-5">Unit / shares on issue</div>
          {isFetching ? (
            <Skeleton width={'70%'} height={28} />
          ) : (
            <div className="text-xl">{goodFormatNumber(info?.['Unit shares'] || 0)}</div>
          )}
        </div>
        {!isFetching && (
          <div className="group-hover/block:block hidden">
            <span
              className="cursor-pointer hover:opacity-70"
              onClick={() =>
                copyTextToClipBoard(goodFormatNumber(info?.['Unit shares'] || 0), {
                  successMessage: 'Copied successfully',
                })
              }
            >
              <img src={CopyIcon} alt="Copy" className="w-6 h-6 min-w-6 min-h-6 " />
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
